import type { ScenarioResult } from '@exigo/shared';
import { formatMoney, formatPercent } from '@exigo/shared';
import { Card } from '../../components/ui/Card';
import styles from './DealValueStrip.module.css';

/**
 * Earning as a share of the value the deal is sized on.
 *
 * Reads `valueBasisCount`, which the engine computes and the strip previously
 * ignored. When an area mixes sizing bases — Security sizes on consumption,
 * seats and MPU whitespace at once — a single ratio would divide by whichever
 * basis happened to come first, so it reports per-row instead of a number that
 * looks authoritative and isn't.
 */
function EarningRatioCell({ result }: { result: ScenarioResult }): React.JSX.Element {
  if (result.valueBasisCount > 1) {
    return (
      <div className={styles.cell}>
        <p className={styles.k}>Earning as % of value</p>
        <p className={styles.v}>Per row</p>
        <p className={styles.s}>
          This area mixes sizing bases, so a single ratio would be misleading. Each engagement shows
          its own.
        </p>
      </div>
    );
  }

  // `acr_total` is the tenant total including the baseline, not a basis anything
  // is measured on, so it is never the denominator.
  const basis = result.contractValueLines.find(
    (l) => l.basis !== 'acr_total' && l.known && l.usd !== null && l.usd > 0,
  );

  return (
    <div className={styles.cell}>
      <p className={styles.k}>Earning as % of year 1 value</p>
      <p className={styles.v}>
        {basis?.usd ? formatPercent((result.claimableTotalUsd / basis.usd) * 100) : '—'}
      </p>
      <p className={styles.s}>
        {basis?.usd
          ? `Of ${formatMoney(basis.usd)} ${basis.label.toLowerCase()}.`
          : 'Needs a contract value to calculate.'}
      </p>
    </div>
  );
}

const BASIS_LABEL: Record<string, string> = {
  acr: 'Net new Azure consumption',
  acr_total: 'Total tenant consumption',
  acv: 'Dynamics 365 ACV',
  seats: 'Seat contract value',
  mpu: 'MPU whitespace',
};

interface DealValueStripProps {
  result: ScenarioResult;
  fx: number;
  /**
   * Only used to show the whole-of-term figure alongside the 12-month one. MCI
   * itself earns on year 1 regardless, so this never scales a payout — it exists
   * so a longer term visibly does something rather than looking broken.
   */
  termMonths: number;
}

export function DealValueStrip({ result, fx, termMonths }: DealValueStripProps): React.JSX.Element {
  const aud = (usd: number | null): string =>
    fx > 0 && usd !== null ? `${formatMoney(Math.round((usd * fx) / 1_000_000))} AUD` : '—';

  return (
    <>
      <Card className={styles.strip}>
        {result.contractValueLines.map((line) => (
          <div className={styles.cell} key={line.basis}>
            <p className={styles.k}>
              {BASIS_LABEL[line.basis] ?? line.label} <span className={styles.per}>&middot; 12 months</span>
            </p>
            <p className={styles.v}>{line.known ? formatMoney(line.usd) : '—'}</p>
            <p className={styles.s}>{line.known ? aud(line.usd) : (line.note ?? '')}</p>
            {/* `known` and `usd` are independent fields, so the null check is
                what actually narrows the type here — not `known`. */}
            {line.usd !== null && termMonths !== 12 && (
              <p className={styles.s}>
                Over {termMonths} months: {formatMoney(Math.round((line.usd * termMonths) / 12))}
              </p>
            )}
          </div>
        ))}
        <div className={styles.cell}>
          <p className={styles.k}>Eligible engagements</p>
          <p className={styles.v}>{result.eligibleCount}</p>
          <p className={styles.s}>of {result.rows.length} in this area</p>
        </div>
        <div className={`${styles.cell} ${styles.cellAccent}`}>
          <p className={styles.k}>Claimable MCI earning</p>
          <p className={styles.v}>{formatMoney(result.claimableTotalUsd)}</p>
          <p className={styles.s}>{aud(result.claimableTotalUsd)}</p>
          <p className={styles.s}>
            {termMonths === 12
              ? 'MCI earns on year 1 only.'
              : `MCI earns on year 1 only — a ${String(termMonths)}-month term does not scale it.`}
          </p>
        </div>

        <EarningRatioCell result={result} />
      </Card>

      {/* The one confusion in this tool that costs real money if a consultant
          makes it: these two figures are different money. */}
      <p className={styles.money}>
        Contract value and MCI earning are <b>different money and must never be added together</b> —
        one is customer revenue, the other is Microsoft fee income. The combined earning figure is a
        ceiling, not a forecast: some engagements are mutually exclusive under Microsoft&rsquo;s
        nomination guidance, several need a prior engagement in the same tenant, and post-sales
        payment depends on the customer actually consuming.
      </p>
    </>
  );
}
